/**
 * Base action types
 */
interface Action<T extends string> {
    type: T;
}

interface ActionWithPayload<T extends string, P extends object>
    extends Action<T> {
    payload: P;
}

/**
 * create actions - the simple, typed way
 */
export function createAction<T extends string>(type: T): Action<T>;
export function createAction<T extends string, P extends object>(
    type: T,
    payload: P
): ActionWithPayload<T, P>;
export function createAction<T extends string, P extends object>(
    type: T,
    payload?: P
) {
    return payload === undefined ? { type } : { type, payload };
}

/**
 * Meta Actions
 * Allow adding meta data to actions
 */
export interface MetaAction<T extends string, M extends object> {
    type: T;
    meta: M;
}

export interface MetaActionWithPayload<
    T extends string,
    M extends object,
    P extends object
> extends MetaAction<T, M> {
    payload: P;
}

export function createMetaAction<T extends string, M extends object>(
    type: T,
    meta: M
): MetaAction<T, M>;
export function createMetaAction<
    T extends string,
    M extends object,
    P extends object
>(type: T, meta: M, payload: P): MetaActionWithPayload<T, M, P>;
export function createMetaAction<
    T extends string,
    M extends object,
    P extends object
>(type: T, meta: M, payload?: P) {
    if (payload === undefined) {
        return { type, meta };
    } else {
        return { type, meta, payload };
    }
}

/**
 * Action Type Union-ator (tm)
 */
type FunctionType = (...args: any[]) => any;
type ActionCreatorsMapObject = { [actionCreator: string]: FunctionType };
export type ActionsUnion<A extends ActionCreatorsMapObject> = ReturnType<
    A[keyof A]
>;
